import { PoseData, GestureEvent, GestureType } from '../../../types';
import { isAbove, isVisible, angleFromVertical } from '../../../utils/geometry';

/**
 * ArmRaisedDetector detects when a single arm is raised vertically above shoulder
 * Validates Requirements 3.3.4
 */
export class ArmRaisedDetector {
  private readonly VERTICAL_ANGLE_THRESHOLD = 30; // ±30 degrees from vertical
  
  /**
   * Detect if left arm is raised vertically
   * @param pose Current pose data
   * @param timestamp Current timestamp
   * @returns GestureEvent if detected, null otherwise
   */
  detectLeft(pose: PoseData, timestamp: number): GestureEvent | null {
    if (!pose.detected) return null;
    
    const { leftWrist, leftElbow, leftShoulder } = pose.landmarks;
    
    // Check if all required landmarks are visible
    if (!isVisible(leftWrist) || !isVisible(leftElbow) || !isVisible(leftShoulder)) {
      return null;
    }
    
    // Check if wrist is above shoulder
    if (!isAbove(leftWrist, leftShoulder)) {
      return null;
    }
    
    // Check if elbow is above shoulder
    if (!isAbove(leftElbow, leftShoulder)) {
      return null;
    }
    
    // Calculate angle from vertical (shoulder to wrist)
    const angleFromVert = angleFromVertical(leftShoulder, leftWrist);
    
    // Check if arm is mostly vertical (within threshold)
    if (angleFromVert > this.VERTICAL_ANGLE_THRESHOLD) {
      return null;
    }
    
    // Calculate confidence based on:
    // 1. Height above shoulder
    // 2. Vertical alignment (closer to 0 degrees = higher confidence)
    // 3. Elbow position
    
    const wristHeightAboveShoulder = leftShoulder.y - leftWrist.y;
    const elbowHeightAboveShoulder = leftShoulder.y - leftElbow.y;
    
    // Height confidence (0.15 units above shoulder = full confidence)
    const heightConfidence = Math.min(1.0, wristHeightAboveShoulder / 0.15);
    
    // Vertical alignment confidence (0 degrees = 1.0, 30 degrees = 0.0)
    const alignmentConfidence = 1.0 - (angleFromVert / this.VERTICAL_ANGLE_THRESHOLD);
    
    // Elbow position confidence
    const elbowConfidence = Math.min(1.0, elbowHeightAboveShoulder / 0.1);
    
    // Overall confidence is weighted average
    const confidence = Math.max(0, Math.min(1, 
      (heightConfidence * 0.4 + alignmentConfidence * 0.4 + elbowConfidence * 0.2)
    ));
    
    return {
      type: GestureType.LEFT_ARM_RAISED,
      confidence,
      timestamp
    };
  }
  
  /**
   * Detect if right arm is raised vertically
   * @param pose Current pose data
   * @param timestamp Current timestamp
   * @returns GestureEvent if detected, null otherwise
   */
  detectRight(pose: PoseData, timestamp: number): GestureEvent | null {
    if (!pose.detected) return null;
    
    const { rightWrist, rightElbow, rightShoulder } = pose.landmarks;
    
    // Check if all required landmarks are visible
    if (!isVisible(rightWrist) || !isVisible(rightElbow) || !isVisible(rightShoulder)) {
      return null;
    }
    
    // Check if wrist is above shoulder
    if (!isAbove(rightWrist, rightShoulder)) {
      return null;
    }
    
    // Check if elbow is above shoulder
    if (!isAbove(rightElbow, rightShoulder)) {
      return null;
    }
    
    // Calculate angle from vertical (shoulder to wrist)
    const angleFromVert = angleFromVertical(rightShoulder, rightWrist);
    
    // Check if arm is mostly vertical (within threshold)
    if (angleFromVert > this.VERTICAL_ANGLE_THRESHOLD) {
      return null;
    }
    
    // Calculate confidence based on:
    // 1. Height above shoulder
    // 2. Vertical alignment (closer to 0 degrees = higher confidence)
    // 3. Elbow position
    
    const wristHeightAboveShoulder = rightShoulder.y - rightWrist.y;
    const elbowHeightAboveShoulder = rightShoulder.y - rightElbow.y;
    
    // Height confidence (0.15 units above shoulder = full confidence)
    const heightConfidence = Math.min(1.0, wristHeightAboveShoulder / 0.15);
    
    // Vertical alignment confidence (0 degrees = 1.0, 30 degrees = 0.0)
    const alignmentConfidence = 1.0 - (angleFromVert / this.VERTICAL_ANGLE_THRESHOLD);
    
    // Elbow position confidence
    const elbowConfidence = Math.min(1.0, elbowHeightAboveShoulder / 0.1);
    
    // Overall confidence is weighted average
    const confidence = Math.max(0, Math.min(1, 
      (heightConfidence * 0.4 + alignmentConfidence * 0.4 + elbowConfidence * 0.2)
    ));
    
    return {
      type: GestureType.RIGHT_ARM_RAISED,
      confidence,
      timestamp
    };
  }
}
